"use client";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/reveal";
import { ExtractBySectionType, SectionType } from "types";
import { PortableText } from "next-sanity";
import { iconsMap } from "@/components/icons";
import { getURL } from "@/lib/utils";
import Link from "next/link";
import { SanityImage } from "@/components/sanity-image";
import { scrollToSignupForm } from "@/lib/conversion-page";

export type CtaSectionType = ExtractBySectionType<SectionType, "ctaSection", "_type">;

export function ClosingCTA({ data, suppressExitLinks = false }: { data: CtaSectionType; suppressExitLinks?: boolean }) {
  const { heading, subheading, description, buttons, image } = data;
  return (
    <section className="my-24 bg-white mx-6 sm:mx-8 lg:mx-24">
      <div className="grid grid-cols-1 grid-rows-1 md:grid-rows-2 lg:grid-rows-1 lg:grid-cols-2 max-w-screen-2xl mx-auto rounded-3xl  shadow-[0px_24.35px_48.7px_-11.69px_rgba(0,_0,_0,_0.25)] overflow-hidden">
        <Reveal className="p-8 lg:p-24 flex flex-col gap-6 relative before:absolute before:content-[''] before:pointer-events-none before:bg-[url('/index/cta/mask-group.svg')] before:bg-no-repeat before:bg-right-bottom before:w-full before:h-full before:bg-contain before:scale-[224%] before:translate-y-[-70%] lg:before:translate-y-[-80%] before:translate-x-[50%] overflow-hidden"
        >
          <div>
            <h3 className="font-medium text-primary-text-700 text-3xl">{heading}</h3>
            <h4 className="font-medium text-secondary-500 text-xl">{subheading}</h4>
          </div>
          {description && (
            <PortableText
              value={description}
              components={{
                block: {
                  normal: ({ children }) => (
                    <p className="font-normal text-primary-text-400 text-lg">{children}</p>
                  ),
                },
              }}
            />
          )}
          <div className="flex flex-row gap-2">
            {buttons.map((button, index) => {
              const href = getURL(button);
              if (suppressExitLinks) {
                return (
                  <Button
                    variant={button.variant}
                    radius="full"
                    key={index}
                    onClick={scrollToSignupForm}
                  >
                    {button.textBeforeIcon}
                    {button.icon && iconsMap[button.icon]}
                    {button.textAfterIcon}
                  </Button>
                );
              }
              return (
                <Button
                  variant={button.variant}
                  radius="full"
                  key={index}
                  asChild={button.link ? true : false}
                >
                  <Link href={href}
                    target={button.openInNewTab ? "_blank" : "_self"}
                    rel={button.openInNewTab ? "noopener noreferrer" : undefined}
                  >
                    {button.textBeforeIcon}
                    {button.icon && iconsMap[button.icon]}
                    {button.textAfterIcon}
                  </Link>
                </Button>
              );
            })}
          </div>
        </Reveal>
        <div className="relative bg-primary-50 hidden md:block">
          {image && (
            <SanityImage
              fill
              alt={image.alt || ""}
              src={image}
              className="object-cover cursor-pointer object-left border-2 border-primary-50"
            />
          )}
        </div>
      </div>
    </section>
  );
}
